<template>
    <Drawer
        v-model:visible="panelVisible"
        position="right"
        header="Notificações"
        class="notificacao-panel"
        :style="{ width: 'min(420px, 100vw)' }"
        @show="onShow"
        @hide="onHide"
    >
        <template #header>
            <div class="flex align-items-center justify-content-between w-full gap-2">
                <span class="font-semibold text-lg">Notificações</span>
                <Button
                    v-if="lista.length"
                    label="Marcar todas"
                    icon="pi pi-check-circle"
                    severity="secondary"
                    size="small"
                    text
                    :loading="isMarcandoTodas"
                    :disabled="!temNaoLidas"
                    @click="marcarTodasLidas"
                />
            </div>
        </template>

        <div v-if="isLoading" class="flex justify-content-center py-5">
            <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="4" />
        </div>

        <div v-else-if="!lista.length" class="text-center text-color-secondary py-5">
            <i class="pi pi-bell-slash text-3xl mb-3 block" aria-hidden="true"></i>
            <p class="m-0">Nenhuma notificação recente.</p>
        </div>

        <ul v-else class="notificacao-list list-none m-0 p-0" role="list">
            <li
                v-for="item in lista"
                :key="item.id"
                class="notificacao-item"
                :class="{ 'notificacao-item--lida': item.lida_em }"
            >
                <button
                    type="button"
                    class="notificacao-item__button"
                    :aria-label="`Abrir notificação: ${item.titulo || item.mensagem}`"
                    @click="abrirNotificacao(item)"
                >
                    <div class="flex align-items-start justify-content-between gap-2">
                        <div class="flex-1 min-w-0 text-left">
                            <div class="font-medium text-sm line-clamp-1">
                                {{ item.titulo || 'Notificação' }}
                            </div>
                            <p v-if="item.mensagem" class="text-sm text-color-secondary mt-1 mb-0 line-clamp-2">
                                {{ item.mensagem }}
                            </p>
                            <small class="text-color-secondary">{{ formatDate(item.created_at) }}</small>
                        </div>
                        <span
                            v-if="!item.lida_em"
                            class="notificacao-item__dot"
                            aria-hidden="true"
                        ></span>
                    </div>
                </button>
            </li>
        </ul>
    </Drawer>
</template>

<script>
import Drawer from 'primevue/drawer';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import Moment from 'moment';

import Notificacao from '@/service/notificacao.js';

export default {
    name: 'NotificacaoPanel',
    components: {
        Drawer,
        Button,
        ProgressSpinner,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:visible', 'count-changed'],
    data() {
        return {
            lista: [],
            isLoading: false,
            isMarcandoTodas: false,
        };
    },
    computed: {
        panelVisible: {
            get() {
                return this.visible;
            },
            set(value) {
                this.$emit('update:visible', value);
            },
        },
        temNaoLidas() {
            return this.lista.some((item) => !item.lida_em);
        },
    },
    methods: {
        onShow() {
            this.loadList();
        },
        onHide() {
            this.lista = [];
        },
        formatDate(value) {
            if (!value) {
                return '—';
            }
            return Moment.utc(value).local().format('DD/MM/YYYY HH:mm');
        },
        loadList() {
            this.isLoading = true;
            Notificacao.list('limit=15')
                .then((response) => {
                    this.lista = Notificacao.parseListResponse(response);
                })
                .catch((error) => {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Ops!',
                        detail: Notificacao.extractErrorMessage(error),
                        life: 5000,
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        marcarLidaSilenciosa(item) {
            if (!item?.id || item.lida_em) {
                return Promise.resolve();
            }

            return Notificacao.marcarLida(item.id)
                .then(() => {
                    item.lida_em = new Date().toISOString();
                })
                .catch(() => {});
        },
        abrirNotificacao(item) {
            this.marcarLidaSilenciosa(item).finally(() => {
                this.emitCountChanged();
                this.panelVisible = false;

                const rota = item.rota || item.route;
                if (!rota) {
                    return;
                }

                const params = item.params || item.rota_params || {};
                const cancelamentoId = item.cancelamento_id
                    ?? (item.referencia_tipo === 'cancelamento' ? item.referencia_id : null);
                if (cancelamentoId && !params.id) {
                    params.id = cancelamentoId;
                }

                this.$router.push({ name: rota, params }).catch(() => {});
            });
        },
        marcarTodasLidas() {
            this.isMarcandoTodas = true;
            Notificacao.marcarTodas()
                .then(() => {
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Sucesso',
                        detail: 'Todas as notificações foram marcadas como lidas.',
                        life: 3000,
                    });
                    this.loadList();
                    this.emitCountChanged();
                })
                .catch((error) => {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Ops!',
                        detail: Notificacao.extractErrorMessage(error),
                        life: 5000,
                    });
                })
                .finally(() => {
                    this.isMarcandoTodas = false;
                });
        },
        emitCountChanged() {
            Notificacao.count()
                .then((response) => {
                    this.$emit('count-changed', Notificacao.parseCountResponse(response));
                })
                .catch(() => {
                    this.$emit('count-changed', 0);
                });
        },
    },
};
</script>

<style scoped>
.notificacao-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.notificacao-item {
    border: 1px solid var(--p-content-border-color, rgba(255, 255, 255, 0.08));
    border-radius: 10px;
    overflow: hidden;
    transition: border-color 160ms ease, background 160ms ease;
}

.notificacao-item:hover {
    border-color: rgba(249, 115, 22, 0.35);
    background: rgba(249, 115, 22, 0.06);
}

.notificacao-item--lida {
    opacity: 0.72;
}

.notificacao-item__button {
    width: 100%;
    padding: 12px 14px;
    background: transparent;
    border: none;
    cursor: pointer;
    color: inherit;
    text-align: left;
}

.notificacao-item__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--p-orange-500, #f97316);
    flex-shrink: 0;
    margin-top: 4px;
}

.line-clamp-1 {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
